import { BaseService } from './base.service';
import { db } from '../data_oscbase';

export class UserService extends BaseService {
  constructor() {
    super('users');
  }

  /**
   * Récupère tous les utilisateurs
   * @returns {Promise<Array>} - Liste de tous les utilisateurs
   */
  async getAll() {
    try {
      return await this.table.toArray();
    } catch (error) {
      console.error('Erreur lors de la récupération des utilisateurs:', error);
      throw error;
    }
  }

  /**
   * Récupère un utilisateur par son ID
   * @param {number} id - ID de l'utilisateur
   * @returns {Promise<Object>} - Utilisateur trouvé
   */
  async getById(id) {
    try {
      return await this.table.get(id);
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Récupère un utilisateur par son nom d'utilisateur
   * @param {string} username - Nom d'utilisateur
   * @returns {Promise<Object>} - Utilisateur trouvé
   */
  async getByUsername(username) {
    try {
      return await this.table.where('username').equals(username).first();
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur par nom:', error);
      throw error;
    }
  }

  /**
   * Récupère un utilisateur par son email
   * @param {string} email - Email de l'utilisateur
   * @returns {Promise<Object>} - Utilisateur trouvé
   */
  async getByEmail(email) {
    try {
      return await this.table.where('email').equals(email).first();
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'utilisateur par email:', error);
      throw error;
    }
  }

  /**
   * Crée un nouvel utilisateur
   * @param {Object} userData - Données de l'utilisateur
   * @returns {Promise<number>} - ID de l'utilisateur créé
   */
  async createUser(userData) {
    try {
      // Vérifier si l'utilisateur existe déjà
      const existingUser = await this.getByUsername(userData.username);
      if (existingUser) {
        throw new Error('Un utilisateur avec ce nom d\'utilisateur existe déjà');
      }

      if (userData.email) {
        const existingEmail = await this.getByEmail(userData.email);
        if (existingEmail) {
          throw new Error('Un utilisateur avec cet email existe déjà');
        }
      }

      // Initialiser les dates de création et mise à jour
      const newUser = {
        ...userData,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const userId = await this.create(newUser);
      return userId;
    } catch (error) {
      console.error('Erreur lors de la création de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Met à jour un utilisateur existant
   * @param {number} userId - ID de l'utilisateur
   * @param {Object} updates - Données à mettre à jour
   * @returns {Promise<Object>} - Utilisateur mis à jour
   */
  async updateUser(userId, updates) {
    try {
      // Vérifier si l'utilisateur existe
      const existingUser = await this.getById(userId);
      if (!existingUser) {
        throw new Error('Utilisateur non trouvé');
      }

      // Vérifier l'unicité du nom d'utilisateur si modifié
      if (updates.username && updates.username !== existingUser.username) {
        const userWithSameUsername = await this.getByUsername(updates.username);
        if (userWithSameUsername) {
          throw new Error('Un utilisateur avec ce nom d\'utilisateur existe déjà');
        }
      }

      // Vérifier l'unicité de l'email si modifié
      if (updates.email && updates.email !== existingUser.email) {
        const userWithSameEmail = await this.getByEmail(updates.email);
        if (userWithSameEmail) {
          throw new Error('Un utilisateur avec cet email existe déjà');
        }
      }

      // Mettre à jour la date de modification
      updates.updatedAt = new Date();

      const updatedUser = await this.update(userId, updates);
      return updatedUser;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Supprime un utilisateur
   * @param {number} userId - ID de l'utilisateur à supprimer
   * @returns {Promise<boolean>} - true si supprimé avec succès
   */
  async deleteUser(userId) {
    try {
      // Vérifier si l'utilisateur existe
      const user = await this.getById(userId);
      if (!user) {
        throw new Error('Utilisateur non trouvé');
      }

      // Vérifier les dépendances (ventes, paiements, etc.)
      const userSales = await db.sales.where('userId').equals(userId).toArray();
      if (userSales.length > 0) {
        throw new Error('Impossible de supprimer l\'utilisateur car il a des ventes associées');
      }

      const userPayments = await db.payments.where('userId').equals(userId).toArray();
      if (userPayments.length > 0) {
        throw new Error('Impossible de supprimer l\'utilisateur car il a des paiements associés');
      }

      await this.delete(userId);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'utilisateur:', error);
      throw error;
    }
  }

  /**
   * Authentifie un utilisateur
   * @param {string} username - Nom d'utilisateur
   * @param {string} password - Mot de passe
   * @returns {Promise<Object>} - Utilisateur authentifié
   */
  async authenticate(username, password) {
    try {
      const user = await this.getByUsername(username);
      
      if (!user) {
        throw new Error('Utilisateur non trouvé');
      }

      if (user.password !== password) {
        throw new Error('Mot de passe incorrect');
      }

      // Mettre à jour la dernière connexion
      await this.updateUser(user.id, {
        lastLoginAt: new Date()
      });

      return user;
    } catch (error) {
      console.error('Erreur lors de l\'authentification:', error);
      throw error;
    }
  }

  /**
   * Change le mot de passe d'un utilisateur
   * @param {number} userId - ID de l'utilisateur
   * @param {string} currentPassword - Mot de passe actuel
   * @param {string} newPassword - Nouveau mot de passe
   * @returns {Promise<boolean>} - true si le mot de passe a été changé
   */
  async changePassword(userId, currentPassword, newPassword) {
    try {
      const user = await this.getById(userId);
      
      if (!user) {
        throw new Error('Utilisateur non trouvé');
      }

      if (user.password !== currentPassword) {
        throw new Error('Mot de passe actuel incorrect');
      }

      await this.updateUser(userId, {
        password: newPassword
      });

      return true;
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      throw error;
    }
  }
}

// Instance par défaut pour la compatibilité
export const userService = new UserService(); 